import { LockOutlined } from "@ant-design/icons";
import { Button, Tooltip, Typography } from "antd";
import { useRef } from "react";
import api from "../api/axios";
import { useView } from "../contexts/ViewContext";
import { ChangePill, jumpToRow, useOffscreenMarkedRows } from "../components/spine/ChangePill";
import {
  Marker,
  NotInPlanMarker,
  Spine,
  SubtopicList,
  SubtopicRow,
  TopicGroup,
  TopicHeader,
  TopicName,
  spineStyles,
} from "../components/spine/Spine";

import styles from "./Recalibration.module.css";

const { Title, Paragraph, Text } = Typography;

const countChanges = (subtopics) =>
  subtopics.filter(
    (subtopic) =>
      subtopic.diff_state === "removed" || subtopic.diff_state === "added",
  ).length;

const ResumeBanner = ({ resume }) => {
  if (!resume) return null;

  const bannerClass = resume.abandoned_subtopic_name
    ? `${styles.resumeBanner} ${styles.resumeBannerAbandon}`
    : styles.resumeBanner;

  return (
    <div className={bannerClass}>
      <Paragraph className={styles.resumeLine}>
        {resume.abandoned_subtopic_name ? (
          <>
            You're dropping <Text strong>{resume.abandoned_subtopic_name}</Text>{" "}
            and starting at <Text strong>{resume.subtopic_name}</Text>.
          </>
        ) : (
          <>
            You'll resume at <Text strong>{resume.subtopic_name}</Text>.
          </>
        )}
      </Paragraph>
    </div>
  );
};

const RecalSubtopicRow = ({ subtopic }) => {
  const accent = ["removed", "added"].includes(subtopic.diff_state)
    ? subtopic.diff_state
    : undefined;

  const leading = (
    <span className={styles.leadingSlot}>
      {subtopic.diff_state === "frozen" ? (
        <LockOutlined
          className={styles.frozenIcon}
          title="Already completed — can't change"
        />
      ) : subtopic.included_live ? (
        <Marker className={styles.markerPlain} />
      ) : (
        <NotInPlanMarker />
      )}
    </span>
  );

  return (
    <SubtopicRow
      data-marked={Boolean(accent)}
      className={styles.subtopicRow}
      accent={accent}
      leading={leading}
    >
      <Text className={spineStyles.subtopicName}>{subtopic.name}</Text>
    </SubtopicRow>
  );
};

const RecalTopicGroup = ({ topic }) => {
  const changeCount = countChanges(topic.subtopics);

  return (
    <TopicGroup>
      <TopicHeader>
        <TopicName>{topic.name}</TopicName>
        {changeCount > 0 && (
          <Text className={styles.topicChangeCount}>
            {changeCount} change{changeCount > 1 ? "s" : ""}
          </Text>
        )}
      </TopicHeader>
      <SubtopicList>
        {topic.subtopics.map((subtopic, index) => (
          <RecalSubtopicRow key={index} subtopic={subtopic} />
        ))}
      </SubtopicList>
    </TopicGroup>
  );
};

const Recalibration = ({ learningJourneyId, view }) => {
  const { readView, setView } = useView();
  const contentRef = useRef(null);
  const { topics, resume } = view.view_data;
  const totalChanges = topics.reduce(
    (count, topic) => count + countChanges(topic.subtopics),
    0,
  );
  const offscreenMarked = useOffscreenMarkedRows(contentRef, [view]);

  const discard = async () => {
    await api.post("/workspace/recalibration/abort/", {
      learning_journey_id: learningJourneyId,
      view_id: view.view_id,
    });
    setView(await readView());
  };

  const accept = async () => {
    await api.post("/workspace/recalibration/accept/", {
      learning_journey_id: learningJourneyId,
      view_id: view.view_id,
    });
    setView(await readView());
  };

  return (
    <div className={styles.page}>
      <div className={styles.content} ref={contentRef}>
        <Title level={2} className={styles.pageTitle}>
          Adjusting your plan
        </Title>
        {totalChanges > 0 && (
          <Paragraph className={styles.changeSummary}>
            {totalChanges} change{totalChanges > 1 ? "s" : ""} proposed
          </Paragraph>
        )}
        <ResumeBanner resume={resume} />
        <Spine>
          {topics.map((topic, index) => (
            <RecalTopicGroup key={index} topic={topic} />
          ))}
        </Spine>
        {offscreenMarked.length > 0 && (
          <ChangePill
            direction={offscreenMarked[0].direction}
            count={offscreenMarked.length}
            onClick={() => jumpToRow(offscreenMarked[0].row)}
          />
        )}
        <div className={styles.actionBar}>
          <Button
            type="text"
            className={styles.discardButton}
            onClick={discard}
          >
            Discard changes
          </Button>
          <Tooltip title={totalChanges === 0 ? "No changes yet" : null}>
            <Button
              type="primary"
              className={styles.acceptButton}
              onClick={accept}
              disabled={totalChanges === 0}
            >
              Accept plan
            </Button>
          </Tooltip>
        </div>
      </div>
    </div>
  );
};

export default Recalibration;
